import React from "react";
import { useEffect } from "react";
import Modal from "react-responsive-modal";
import { useParams } from "react-router-dom";
import SurveyService from "services/SurveyService";
import HandleSurveyQuestions from "./HandleSurveyQuestions";
import { useFormik } from "formik";
import * as Yup from "yup";
import ButtonWithProgress from "components/ButtonWithProgress";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { useToasts } from "react-toast-notifications";

export default ({
  onClose,
  teamQuestions,
  prePopulatedQuestions,
  dispatch,
  isSubmittingAns,
  activeSurvey,
  reload,
  addedCustomQuestionsCount,
}) => {
  const params = useParams();
  const { addToast } = useToasts();

  // Just save the output of the form to be
  const createQuestions = async ({ questions }) => {
    if (questions.length > 0) {
      const payload = {
        surveyId: Number(activeSurvey),
        teamId: Number(params.teamId),
        question: questions.map((q) => {
          const baseQuestion = {
            type: q.type,
            question: q.questions,
            isCustomCreated: q.isCustomCreated,
          };
          
          // Add minValue and maxValue for range type questions
          if (q.type === "range") {
            baseQuestion.minValue = Number(q.minValue) || 1;
            baseQuestion.maxValue = Number(q.maxValue) || 10;
          }
          
          // Add options for fixed type questions
          if (q.type === "fixed") {
            baseQuestion.options = (q.options || [])
              .filter(option => option.option && option.option.trim() !== "")
              .map(option => option.option);
          }
          
          return baseQuestion;
        }),
      };
      try {
        dispatch({ type: "setIsSubmittingAns", value: true });
        await SurveyService.createSurveyQuestion(payload);
        addToast("Survey questions added.", { appearance: "success" });

        dispatch({ type: "setIsSubmittingAns", value: false });
        dispatch({ type: "setReload", value: !reload });
        onClose();
      } catch {
        addToast("Some error occurred, while saving questions.", {
          appearance: "error",
        });
        dispatch({ type: "setIsSubmittingAns", value: false });
      }
    }
  };

  // Define the formik hook
  const formik = useFormik({
    initialValues: {
      questions: [],
    },
    validationSchema: Yup.object().shape({
      questions: Yup.array().of(
        Yup.object().shape({
          questions: Yup.string().required("Please enter question"),
          type: Yup.string().required("Please select type"),
          minValue: Yup.number().when("type", {
            is: "range",
            then: Yup.number()
              .required("Min value is required for range type")
              .typeError("Min value must be a number")
              .test(
                "is-less-than-max",
                "Min value must be less than max value",
                function (value) {
                  const { maxValue } = this.parent;
                  return !maxValue || value < maxValue;
                }
              ),
            otherwise: Yup.number().notRequired(),
          }),
          maxValue: Yup.number().when("type", {
            is: "range",
            then: Yup.number()
              .required("Max value is required for range type")
              .typeError("Max value must be a number")
              .test(
                "is-greater-than-min",
                "Max value must be greater than min value",
                function (value) {
                  const { minValue } = this.parent;
                  return !minValue || value > minValue;
                }
              ),
            otherwise: Yup.number().notRequired(),
          }),
          options: Yup.array().when("type", {
            is: "fixed",
            then: Yup.array()
              .of(
                Yup.object().shape({
                  option: Yup.string()
                    .required("Option text is required")
                    .min(1, "Option text cannot be empty")
                })
              )
              .min(2, "At least 2 options are required for fixed type")
              .test(
                "unique-options",
                "All options must be unique",
                function (options) {
                  if (!options) return true;
                  const optionTexts = options.map(opt => opt.option?.toLowerCase().trim());
                  const uniqueTexts = [...new Set(optionTexts)];
                  return optionTexts.length === uniqueTexts.length;
                }
              ),
            otherwise: Yup.array().notRequired(),
          }),
        })
      ),
    }),
    onSubmit: (values) => createQuestions(values),
    validateOnBlur: true,
  });

  const totalAddedCustomQuestions =
    (formik.values.questions.filter((s) => s.isCustomCreated === 1) || [])
      .length + addedCustomQuestionsCount;

  useEffect(() => {
    (async () => {
      try {
        const resp = await SurveyService.getQuestionToAdd(params.teamId);
        dispatch({
          type: "allQuestionsToAdd",
          value: resp.data.data,
        });
      } catch {}
    })();
  }, [params.teamId, dispatch]);

  const isSelected = (item) => {
    const check = formik.values.questions.find((e) => e.id === item.id);
    return check ? true : false;
  };

  const handleSelected = (item) => {
    // if (formik.values.questions.length === totalAllowedQuestions) {
    //   addToast("You can not add more than 3 questions.", {
    //     appearance: "error",
    //   });
    //   return;
    // }
    if (item.isCustomCreated === 1) {
      if (totalAddedCustomQuestions !== 5) {
        const qust = formik.values.questions.push(item);
        formik.setFieldValue({ ...formik.values, qust });
      } else {
        addToast("You can not add more than 5 custom questions.", {
          appearance: "error",
        });
      }
    } else {
      const qust = formik.values.questions.push(item);
      formik.setFieldValue({ ...formik.values, qust });
    }
  };

  const addCustomQue = () => {
    // if (formik.values.questions.length === totalAllowedQuestions) {
    //   addToast("You can not add more than 3 questions.", {
    //     appearance: "error",
    //   });
    //   return;
    // }
    const tmpId = Math.floor(Date.now());
    const lengthComplete = totalAddedCustomQuestions === 5;
    if (!lengthComplete) {
      const qust = formik.values.questions.push({
        id: tmpId,
        questions: "",
        type: "text",
        isCustomCreated: 1,
      });
      formik.setFieldValue({ ...formik.values, qust });
    }
  };

  return (
    <Modal
      center
      open
      closeOnEsc={false}
      closeOnOverlayClick={false}
      styles={{
        modal: {
          maxWidth: "80%",
          width: "80%",
          borderRadius: "10px",
        },
      }}
      onClose={onClose}
    >
      <div className="addQuestionModal">
        <form noValidate onSubmit={formik.handleSubmit}>
          <div className="riddleModalHeading">
            <h6>
              <span className="riddleModalHText">Survey Questions</span>
            </h6>
          </div>
          <div className="d-flex gap-2">
            <div className="p-2 float-left">
              <h6 class="list-group-item-heading">Standard questions</h6>
              <ul
                className="list-group customScrollArea"
                style={{
                  maxHeight: "500px",
                  minHeight: "300px",
                  overflowY: "scroll",
                  maxWidth: "300px",
                  minWidth: "300px",
                }}
              >
                {prePopulatedQuestions.map((item) => (
                  <li
                    className={`list-group-item ${
                      isSelected(item) ? `selectedQuestionItem` : ``
                    }`}
                    onClick={() => !isSelected(item) && handleSelected(item)}
                  >
                    <div className="d-flex justify-content-between">
                      <div>{item.questions}</div>
                    </div>
                  </li>
                ))}
                {prePopulatedQuestions.length === 0 && (
                  <li className={`list-group-item`}>
                    <div className="d-flex justify-content-between">
                      <div>No pre-populated questions found.</div>
                    </div>
                  </li>
                )}
              </ul>
            </div>

            <div className="d-flex flex-column" style={{ width: "100%" }}>
              <div className="mt-1 d-flex justify-content-start">
                <button
                  className="addQuestionBtn btn-sm mb-2"
                  onClick={addCustomQue}
                  disabled={totalAddedCustomQuestions === 5}
                >
                  <FontAwesomeIcon
                    icon={faPlusCircle}
                    style={{ fontSize: "1.3rem" }}
                  />{" "}
                  Add Question
                </button>
              </div>
              <div className="d-flex justify-content-center">
                {formik.values.questions.length === 0 && (
                  <div className="d-flex justify-content-center mt-3">
                    Select questions from provided list or you can create your
                    own question by clicking on Add button
                  </div>
                )}
              </div>
              <div>
                <ul className="list-group questionSection customScrollArea">
                  {formik.values.questions.map((item, iDx) => (
                    <HandleSurveyQuestions formik={formik} index={iDx} />
                  ))}
                </ul>
              </div>
            </div>

            <div>
              <h6 class="list-group-item-heading mt-2">Custom questions</h6>
              <ul
                className="list-group customScrollArea"
                style={{
                  maxHeight: "500px",
                  minHeight: "300px",
                  overflowY: "scroll",
                  maxWidth: "300px",
                  minWidth: "300px",
                }}
              >
                {teamQuestions.map((item) => (
                  <li
                    className={`list-group-item ${
                      isSelected(item) ? `selectedQuestionItem` : ``
                    }`}
                    onClick={() => !isSelected(item) && handleSelected(item)}
                  >
                    <div className="d-flex justify-content-between">
                      <div>{item.questions}</div>
                    </div>
                  </li>
                ))}
                {teamQuestions.length === 0 && (
                  <li className={`list-group-item`}>
                    <div className="d-flex justify-content-between">
                      <div>You dont have any team questions created.</div>
                    </div>
                  </li>
                )}
              </ul>
            </div>
          </div>
          <div className="d-flex justify-content-center">
            <ButtonWithProgress
              initialText={"Submit"}
              message={"Submitting Questions"}
              isSubmitting={isSubmittingAns}
              buttonWithProgressClass="w-50 btn grad_btn mt-3"
              type="submit"
              isValid={formik.values.questions.length !== 0}
            />
          </div>
        </form>
      </div>
    </Modal>
  );
};
